import { TwilioPhoneNumberModel } from "@jugl-web/rest-api";
import { Button, PlainButton, Dialog } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import React, { ReactNode, useEffect, useState } from "react";
import { ReactComponent as TwilioIcon } from "./assets/twilio.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

interface SelectPhoneNumberDialogProps {
  isOpen: boolean;
  isLoading: boolean;
  phoneNumbers: TwilioPhoneNumberModel[];
  initialSelectedSid?: string;
  isAssociating: boolean;
  onAssociate: (phoneNumber: TwilioPhoneNumberModel) => void;
  onClose: () => void;
}

export const SelectPhoneNumberDialog: React.FC<
  SelectPhoneNumberDialogProps
> = ({
  isOpen,
  isLoading,
  phoneNumbers,
  initialSelectedSid,
  isAssociating,
  onAssociate,
  onClose,
}) => {
  const { t } = useTranslations();

  const [selectedSid, setSelectedSid] = useState<string | undefined>(
    initialSelectedSid
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedSid(initialSelectedSid);
    }
  }, [isOpen, initialSelectedSid]);

  const selectedPhoneNumber = phoneNumbers.find(
    (item) => item.sid === selectedSid
  );

  const handleAssociate = () => {
    if (selectedPhoneNumber) {
      onAssociate(selectedPhoneNumber);
    }
  };

  const isEmpty = !isLoading && phoneNumbers.length === 0;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className="flex w-full max-w-[560px] flex-col font-secondary"
    >
      <div className="flex h-[60px] w-full items-center justify-between border-0 border-b border-solid border-dark-100 px-6">
        <div className="flex items-center gap-3">
          <TwilioIcon className="h-6 w-6" />
          <span className="text-lg font-semibold text-[#383838]">
            {t({
              id: "client-support-page.select-phone-number",
              defaultMessage: "Select phone number",
            })}
          </span>
        </div>
        <PlainButton
          onClick={onClose}
          className="flex items-center justify-center text-grey-700"
        >
          <CloseIcon />
        </PlainButton>
      </div>

      <div className="flex max-h-[calc(90vh-140px)] min-h-[180px] flex-col gap-4 overflow-y-auto p-6">
        {isLoading ? (
          <>
            <div className="h-[52px] w-full animate-pulse rounded-xl bg-grey-200" />
            <div className="h-[52px] w-3/5 animate-pulse rounded-xl bg-grey-200" />
            <div className="h-[52px] w-2/5 animate-pulse rounded-xl bg-grey-200" />
          </>
        ) : isEmpty ? (
          <div className="flex flex-col gap-1">
            <span className="font-secondary text-base font-medium text-[#383838]">
              {t({
                id: "client-support-page.no-twilio-phone-numbers-found",
                defaultMessage: "No Twilio Phone Numbers were found",
              })}
            </span>
            <span className="font-secondary text-sm text-[#737B7E]">
              {t(
                {
                  id: "client-support-page.no-twilio-phone-numbers-hint",
                  defaultMessage:
                    "Visit your <link>Twilio account</link> to connect phone numbers and proceed",
                },
                {
                  link: (text: ReactNode) => (
                    <a
                      className="font-medium text-primary underline"
                      href="https://console.twilio.com"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {text}
                    </a>
                  ),
                }
              )}
            </span>
          </div>
        ) : (
          <>
            <span className="font-secondary text-sm text-[#737B7E]">
              {t({
                id: "client-support-page.select-twilio-phone-number-hint",
                defaultMessage:
                  "Select your Twilio phone number to associate with Jugl",
              })}
            </span>
            {phoneNumbers.map((phoneNumber) => {
              const isSelected = phoneNumber.sid === selectedSid;
              return (
                <PlainButton
                  key={phoneNumber.sid}
                  onClick={() => setSelectedSid(phoneNumber.sid)}
                  className={cx(
                    "flex w-full items-center gap-3 rounded-xl border border-solid px-4 py-3.5 transition-colors",
                    isSelected ? "border-dark-700" : "border-dark-100"
                  )}
                >
                  <span
                    className={cx(
                      "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border border-solid",
                      isSelected ? "border-dark-700" : "border-grey-500"
                    )}
                  >
                    {isSelected && (
                      <span className="h-2.5 w-2.5 rounded-full bg-dark-700" />
                    )}
                  </span>
                  <span className="font-secondary text-base text-[#383838]">
                    {phoneNumber.phone_number}
                  </span>
                </PlainButton>
              );
            })}
          </>
        )}
      </div>

      <div className="flex h-[76px] w-full items-center justify-end gap-3 border-0 border-t border-solid border-dark-100 px-6">
        <Button variant="tertiary" className="h-10 w-[120px]" onClick={onClose}>
          {t({ id: "common.cancel", defaultMessage: "Cancel" })}
        </Button>
        <Button
          variant="primary"
          className="h-10 w-[160px]"
          isDisabled={!selectedPhoneNumber || isAssociating}
          isLoading={isAssociating}
          iconEnd={<ArrowRightIcon />}
          onClick={handleAssociate}
        >
          {t({
            id: "client-support-page.associate",
            defaultMessage: "Associate",
          })}
        </Button>
      </div>
    </Dialog>
  );
};
